import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ArrowLeftIcon,
    EyeIcon,
    EyeSlashIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { createAdmin, getPermissionsCatalog } from "../api/adminsApi";
import type { CreateAdminRequest } from "../types";
import { PermissionCatalogEditor } from "../ui/PermissionCatalogEditor";
import type { PermissionCode } from "@/features/auth/permissions";

export const CreateAdminRoute = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [showPassword, setShowPassword] = useState(false);
    const [selectedPermissions, setSelectedPermissions] = useState<PermissionCode[]>([]);
    const [formData, setFormData] = useState<CreateAdminRequest>({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        permissions: [],
    });
    const [errors, setErrors] = useState<
        Partial<Record<keyof CreateAdminRequest | "permissions", string>>
    >({});

    const permissionCatalogQuery = useQuery({
        queryKey: ["permissions-catalog"],
        queryFn: getPermissionsCatalog,
    });

    const permissionCatalog = permissionCatalogQuery.data?.data.permissions ?? {};

    const createMutation = useMutation({
        mutationFn: createAdmin,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admins"] });
            navigate("/admin-management");
        },
    });

    const validateForm = () => {
        const nextErrors: Partial<Record<keyof CreateAdminRequest, string>> = {};

        if (!formData.firstName.trim()) {
            nextErrors.firstName = "First name is required";
        }

        if (!formData.lastName.trim()) {
            nextErrors.lastName = "Last name is required";
        }

        if (!formData.email.trim()) {
            nextErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            nextErrors.email = "Invalid email format";
        }

        if (!formData.password) {
            nextErrors.password = "Password is required";
        } else if (formData.password.length < 8) {
            nextErrors.password = "Password must be at least 8 characters";
        }

        if (selectedPermissions.length === 0) {
            nextErrors.permissions = "Select at least one permission.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleChange = (field: keyof CreateAdminRequest, value: string) => {
        setFormData((previous) => ({ ...previous, [field]: value }));
        if (errors[field]) {
            setErrors((previous) => ({ ...previous, [field]: undefined }));
        }
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (validateForm()) {
            createMutation.mutate({
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                password: formData.password,
                permissions: selectedPermissions,
            });
        }
    };

    return (
        <div className="space-y-6">
            <div className="space-y-3">
                <Link
                    to="/admin-management"
                    className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline dark:text-primary-400"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to Admin Management
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                        Create Admin
                    </h1>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Create a new administrator account with email-based access.
                    </p>
                </div>
            </div>

            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Admin Details
                    </h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Create a new administrator with explicit, backend-approved permissions.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5 p-6">
                    <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
                        <div className="space-y-5">
                            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                        First Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.firstName}
                                        onChange={(event) =>
                                            handleChange("firstName", event.target.value)
                                        }
                                        placeholder="Jane"
                                        className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                            errors.firstName
                                                ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                                : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                        }`}
                                    />
                                    {errors.firstName ? (
                                        <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                            {errors.firstName}
                                        </p>
                                    ) : null}
                                </div>

                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.lastName}
                                        onChange={(event) =>
                                            handleChange("lastName", event.target.value)
                                        }
                                        placeholder="Admin"
                                        className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                            errors.lastName
                                                ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                                : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                        }`}
                                    />
                                    {errors.lastName ? (
                                        <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                            {errors.lastName}
                                        </p>
                                    ) : null}
                                </div>
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(event) =>
                                        handleChange("email", event.target.value)
                                    }
                                    placeholder="jane.admin@company.com"
                                    className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                        errors.email
                                            ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                            : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                    }`}
                                />
                                {errors.email ? (
                                    <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                        {errors.email}
                                    </p>
                                ) : null}
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={(event) =>
                                            handleChange("password", event.target.value)
                                        }
                                        placeholder="Minimum 8 characters"
                                        className={`w-full rounded-xl border px-4 py-3 pr-11 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                                            errors.password
                                                ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                                                : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                                        }`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowPassword((previous) => !previous)
                                        }
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                    >
                                        {showPassword ? (
                                            <EyeSlashIcon className="h-5 w-5" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                                {errors.password ? (
                                    <p className="mt-1.5 text-sm text-rose-600 dark:text-rose-400">
                                        {errors.password}
                                    </p>
                                ) : (
                                    <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                                        Recommended: uppercase, lowercase, digit, special character.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="space-y-4">
                            {permissionCatalogQuery.isLoading ? (
                                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-10 text-center dark:border-slate-800 dark:bg-slate-900/50">
                                    <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
                                    <p className="text-sm text-slate-500 dark:text-slate-400">
                                        Loading permission catalog...
                                    </p>
                                </div>
                            ) : permissionCatalogQuery.isError ? (
                                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-6 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                                    <div className="flex items-start gap-3">
                                        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                                        <div>
                                            <p className="font-medium">
                                                Failed to load permission catalog.
                                            </p>
                                            <p className="mt-1">
                                                Retry this page before creating the admin, so explicit permissions can be assigned correctly.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <PermissionCatalogEditor
                                    catalog={permissionCatalog}
                                    selectedPermissions={selectedPermissions}
                                    onChange={(permissions) => {
                                        setSelectedPermissions(permissions);
                                        if (errors.permissions) {
                                            setErrors((previous) => ({
                                                ...previous,
                                                permissions: undefined,
                                            }));
                                        }
                                    }}
                                    disabled={createMutation.isPending}
                                />
                            )}

                            {errors.permissions ? (
                                <p className="text-sm text-rose-600 dark:text-rose-400">
                                    {errors.permissions}
                                </p>
                            ) : null}
                        </div>
                    </div>

                    {createMutation.isError ? (
                        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                            {(createMutation.error as { response?: { data?: { message?: string } } })
                                ?.response?.data?.message ||
                                "Failed to create admin. Please try again."}
                        </div>
                    ) : null}

                    <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-5 dark:border-slate-800">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate("/admin-management")}
                            disabled={createMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={createMutation.isPending}>
                            {createMutation.isPending ? "Creating..." : "Create Admin"}
                        </Button>
                    </div>
                </form>
            </section>
        </div>
    );
};
